/**
 * WHAT A BUTTON IS ALLOWED TO DRAW — the resolver both CTA primitives run their label through.
 *
 * ───────────────────────────────────────────────────────────────────────────────────────────────────────────
 * **THE DEFECT THIS EXISTS FOR.** Evan, on a device, 2026-09-16: *"three CTAs that render as blank green
 * buttons before the parent types."* A wasabi fill, the 2px `#170909` border and the 4px hard plate all drew;
 * the text did not. Every one of the three was the same sentence written three times:
 *
 * ```tsx
 * label={name ? saveChild(name) : ''}                       // P04, until the parent types a first name
 * const ctaLabel = count >= 2 ? addChores(count) : … : '';  // P05, until a chore is picked
 * firstName && product?.bbPrice != null ? addToRewards(…) : ''  // P13b, with no child or no BB rate
 * ```
 *
 * Each screen had reasoned correctly that it had no sentence to draw — the copy deck's merge-tag rule is that
 * *a value the app has not been GIVEN does not draw at all* — and then handed the nothing to a button, which
 * drew itself around it. **The screens were three call sites; the button accepting an empty label and drawing
 * anyway was the defect.** Fixing the three and leaving the fourth to be written is how it comes back, so the
 * refusal lives here, where `WasabiButton` and `ChildButton` both reach it.
 *
 * ───────────────────────────────────────────────────────────────────────────────────────────────────────────
 * **THE RULE. A control draws when it has something to SHOW and something to SAY.**
 *
 *  · Something to show is a non-empty label, or face content that stands in for one — a `leading` glyph, or
 *    the loading spinner that replaces the label while a write is in flight.
 *  · Something to say is the label itself, or an explicit `accessibilityLabel` when the face carries no text.
 *
 * Anything else is a bug, and a bug does NOT draw: an unnamed control is worse than a missing one, because a
 * missing one is visible as missing while an unnamed one invites a tap whose outcome nobody can predict, and
 * reads to VoiceOver as "button" and nothing else. Not drawing is also what every screen in this app already
 * does by hand for the same reason — `{view.cta ? <WasabiButton … /> : null}` on M06, C12 and G04 — so the
 * component is adopting the house idiom rather than inventing one.
 *
 * **An `accessibilityLabel` alone is NOT a licence to draw an empty face**, and that is the case worth stating
 * because it had already been "fixed" once: P13b passed `accessibilityLabel={ctaLabel || a11y.fund.addGiftcard}`
 * so the control had a spoken name — and still drew a blank green plate for every sighted parent. A spoken
 * name repairs a face that carries a GLYPH. It does not repair a face that carries nothing.
 *
 * **Why not a fallback label instead.** CLAUDE.md: *"Invent copy: strings come from COPY_DECK.md; ask when
 * missing."* A generic "Continue" under a button whose whole job is to name a value ("Save Ruby", "Pay $34.00
 * and clear the balance") would be invented copy AND a worse button. The screen either has the value or has no
 * control to offer yet.
 *
 * Nothing here touches React or react-native, so the rule is a table in `__tests__/buttonLabel.test.ts` rather
 * than something only a device can see — the same reason `faces.ts` is pure.
 */

/** Vite (portal) defines no `__DEV__`; Metro does. Restated from `../a11y` rather than imported: this module is the pure half and importing the a11y module for one env probe would make every consumer of the rule pull in the layout assertions too. */
const isDev: boolean = typeof __DEV__ !== 'undefined' && __DEV__;

export type ButtonLabelKind =
  /** A face with text in it. The ordinary case. */
  | 'labelled'
  /** A face with a glyph or a spinner in it and an explicit spoken name. */
  | 'glyph'
  /** Nothing to show, nothing to say, or something to show with nothing to say. Does not draw. */
  | 'blank';

export interface ButtonLabelling {
  /** What the face draws. */
  label: string;
  /** What VoiceOver says. Required when the face carries no text; otherwise the label speaks for itself. */
  accessibilityLabel?: string | undefined;
  /**
   * Something OTHER than the label occupies the face — a `leading` glyph, or the loading spinner that replaces
   * the label mid-write. It makes an empty label legitimate; it does not make an unnamed control legitimate.
   */
  faceContent?: boolean | undefined;
}

export interface ResolvedButtonLabel {
  kind: ButtonLabelKind;
  /** The control draws at all. `false` is the refusal — the caller returns `null`. */
  draws: boolean;
  /** The text the face draws, or `null` when the face carries a glyph or a spinner alone. */
  text: string | null;
  /** The spoken name. `null` only when `draws` is false, because then there is no control to name. */
  accessibilityLabel: string | null;
  /**
   * Why it refused, in words, for a developer. `null` whenever nothing is wrong — so a caller can say
   * `if (resolved.complaint) …` without re-deriving the rule.
   */
  complaint: string | null;
}

/**
 * Resolve what a CTA may draw and what it is called. `name` identifies the control in the dev complaint and is
 * the same string its `assertHitOnLayout` uses, so one screen's two failures read as one control.
 *
 * Surrounding whitespace is not a label: `label={' '}` is as blank on the device as `label={''}`, and a
 * template that joins an empty segment list ends in exactly that.
 */
export function resolveButtonLabel(name: string, input: ButtonLabelling): ResolvedButtonLabel {
  const text = input.label.trim();
  const spoken = (input.accessibilityLabel ?? '').trim();
  const faceContent = input.faceContent === true;

  if (text !== '')
    return {
      kind: 'labelled',
      draws: true,
      text,
      accessibilityLabel: spoken === '' ? text : spoken,
      complaint: null,
    };

  if (faceContent && spoken !== '')
    return { kind: 'glyph', draws: true, text: null, accessibilityLabel: spoken, complaint: null };

  return {
    kind: 'blank',
    draws: false,
    text: null,
    accessibilityLabel: null,
    complaint: faceContent
      ? `${name}: a face with a glyph and no label needs an accessibilityLabel — an icon button that VoiceOver ` +
        `reads as "button" is not a control. It did not draw.`
      : `${name}: label is empty, so the fill and the plate would draw around nothing. Either give the control ` +
        `a label, or do not render it until the screen has the value its label names. It did not draw.`,
  };
}

/**
 * Say the complaint out loud, once, where a developer will see it — and nowhere in release, where it would be
 * a console line nobody reads on a device that has already lost the control.
 *
 * `console.error` rather than `warn`: this is not a style note. A CTA that does not draw is a screen a parent
 * cannot finish, and the whole reason the three blank buttons reached a device is that nothing said anything.
 */
export function reportBlankButton(resolved: ResolvedButtonLabel): void {
  if (isDev && resolved.complaint !== null) console.error(`[kk/ui] ${resolved.complaint}`);
}
